import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, TrendingDown, MessageSquare, X, ChevronRight, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Filter } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Treemap } from 'recharts';
// Region filter options
const regions = [{
  id: 'konzern',
  name: 'Gesamtkonzern',
  icon: Building2
}, {
  id: 'nord',
  name: 'EDEKA Nord'
}, {
  id: 'minden-hannover',
  name: 'EDEKA Minden-Hannover'
}, {
  id: 'rhein-ruhr',
  name: 'EDEKA Rhein-Ruhr'
}, {
  id: 'hessenring',
  name: 'EDEKA Hessenring'
}, {
  id: 'nordbayern',
  name: 'EDEKA Nordbayern-Sachsen-Thüringen'
}, {
  id: 'suedwest',
  name: 'EDEKA Südwest'
}, {
  id: 'suedbayern',
  name: 'EDEKA Südbayern'
}];
// Mock data per region
const regionData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  konzern: {
    positive: 12450,
    neutral: 4230,
    negative: 1820,
    total: 18500,
    avgRating: 4.2,
    trend: 0.15
  },
  nord: {
    positive: 1820,
    neutral: 580,
    negative: 210,
    total: 2610,
    avgRating: 4.4,
    trend: 0.22
  },
  'minden-hannover': {
    positive: 2100,
    neutral: 720,
    negative: 380,
    total: 3200,
    avgRating: 4.1,
    trend: 0.08
  },
  'rhein-ruhr': {
    positive: 2450,
    neutral: 890,
    negative: 460,
    total: 3800,
    avgRating: 4.0,
    trend: -0.05
  },
  hessenring: {
    positive: 1650,
    neutral: 520,
    negative: 180,
    total: 2350,
    avgRating: 4.3,
    trend: 0.18
  },
  nordbayern: {
    positive: 1580,
    neutral: 490,
    negative: 220,
    total: 2290,
    avgRating: 4.2,
    trend: 0.12
  },
  suedwest: {
    positive: 1720,
    neutral: 580,
    negative: 240,
    total: 2540,
    avgRating: 4.3,
    trend: 0.2
  },
  suedbayern: {
    positive: 1130,
    neutral: 450,
    negative: 130,
    total: 1710,
    avgRating: 4.5,
    trend: 0.25
  }
};
// Topic clusters data
const topicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    market: string;
    date: string;
    rating: number;
  }>;
}>> = {
  konzern: [{
    name: 'Frische & Qualität',
    size: 4200,
    sentiment: 'positive',
    comments: [{
      text: 'Die Obst- und Gemüseabteilung ist immer top frisch!',
      market: 'EDEKA Center Hamburg',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Fleischtheke in der Umgebung, sehr gute Beratung.',
      market: 'EDEKA München-Schwabing',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Frische Backwaren jeden Tag, super Qualität.',
      market: 'EDEKA Frankfurt-Nordend',
      date: '2024-01-26',
      rating: 5
    }, {
      text: 'Die Fischtheke ist hervorragend bestückt.',
      market: 'EDEKA Kiel',
      date: '2024-01-25',
      rating: 5
    }, {
      text: 'Regionale Produkte in bester Qualität.',
      market: 'EDEKA Freiburg',
      date: '2024-01-24',
      rating: 4
    }]
  }, {
    name: 'Freundlichkeit',
    size: 3100,
    sentiment: 'positive',
    comments: [{
      text: 'Personal ist immer freundlich und hilfsbereit.',
      market: 'EDEKA Dresden-Altstadt',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Tolle Beratung an der Käsetheke!',
      market: 'EDEKA Stuttgart-West',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Sehr nettes Team, man fühlt sich willkommen.',
      market: 'EDEKA Hannover-List',
      date: '2024-01-26',
      rating: 4
    }]
  }, {
    name: 'Wartezeiten',
    size: 2800,
    sentiment: 'negative',
    comments: [{
      text: 'Zu wenig Kassen offen, lange Wartezeiten.',
      market: 'EDEKA Berlin-Kreuzberg',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Samstags ist es unmöglich hier einzukaufen.',
      market: 'EDEKA Köln-Ehrenfeld',
      date: '2024-01-27',
      rating: 1
    }, {
      text: 'Bitte mehr Personal an den Kassen!',
      market: 'EDEKA Dortmund',
      date: '2024-01-26',
      rating: 2
    }, {
      text: 'Wartezeit von 15 Minuten ist nicht akzeptabel.',
      market: 'EDEKA Duisburg',
      date: '2024-01-25',
      rating: 2
    }]
  }, {
    name: 'Sortiment',
    size: 2400,
    sentiment: 'positive',
    comments: [{
      text: 'Riesige Auswahl, findet man alles was man braucht.',
      market: 'EDEKA Center München',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Tolles Bio-Sortiment!',
      market: 'EDEKA Frankfurt-Sachsenhausen',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Preise',
    size: 2100,
    sentiment: 'neutral',
    comments: [{
      text: 'Preise sind okay, nicht günstig aber fair.',
      market: 'EDEKA Leipzig-Zentrum',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Für die Qualität angemessene Preise.',
      market: 'EDEKA Nürnberg',
      date: '2024-01-27',
      rating: 4
    }, {
      text: 'Etwas teurer als Discounter, aber bessere Qualität.',
      market: 'EDEKA Bremen',
      date: '2024-01-26',
      rating: 3
    }]
  }, {
    name: 'Sauberkeit',
    size: 1800,
    sentiment: 'positive',
    comments: [{
      text: 'Immer sauber und ordentlich.',
      market: 'EDEKA Karlsruhe',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Gepflegter Markt, macht Spaß hier einzukaufen.',
      market: 'EDEKA Düsseldorf-Bilk',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Parkplatz',
    size: 1500,
    sentiment: 'negative',
    comments: [{
      text: 'Parkplatzsituation ist katastrophal.',
      market: 'EDEKA Berlin-Mitte',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Zu wenig Parkplätze, besonders abends.',
      market: 'EDEKA Hamburg-Altona',
      date: '2024-01-27',
      rating: 3
    }]
  }, {
    name: 'Öffnungszeiten',
    size: 1200,
    sentiment: 'neutral',
    comments: [{
      text: 'Schade dass sonntags geschlossen ist.',
      market: 'EDEKA Essen',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Öffnungszeiten könnten länger sein.',
      market: 'EDEKA Bonn',
      date: '2024-01-27',
      rating: 3
    }]
  }],
  nord: [{
    name: 'Frische & Qualität',
    size: 620,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Freundlichkeit',
    size: 480,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Fischtheke',
    size: 380,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Wartezeiten',
    size: 280,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Sortiment',
    size: 350,
    sentiment: 'positive',
    comments: []
  }]
  // Add more regions as needed...
};
// 12-month trend data
const monthlyTrendData: Record<string, Array<{
  month: string;
  rating: number;
  reviews: number;
}>> = {
  konzern: [{
    month: 'Feb 23',
    rating: 4.05,
    reviews: 14200
  }, {
    month: 'Mär 23',
    rating: 4.08,
    reviews: 15100
  }, {
    month: 'Apr 23',
    rating: 4.06,
    reviews: 14800
  }, {
    month: 'Mai 23',
    rating: 4.1,
    reviews: 15500
  }, {
    month: 'Jun 23',
    rating: 4.12,
    reviews: 16200
  }, {
    month: 'Jul 23',
    rating: 4.09,
    reviews: 15800
  }, {
    month: 'Aug 23',
    rating: 4.11,
    reviews: 16000
  }, {
    month: 'Sep 23',
    rating: 4.14,
    reviews: 16500
  }, {
    month: 'Okt 23',
    rating: 4.13,
    reviews: 17200
  }, {
    month: 'Nov 23',
    rating: 4.16,
    reviews: 17800
  }, {
    month: 'Dez 23',
    rating: 4.18,
    reviews: 18200
  }, {
    month: 'Jan 24',
    rating: 4.2,
    reviews: 18500
  }],
  nord: [{
    month: 'Feb 23',
    rating: 4.2,
    reviews: 2100
  }, {
    month: 'Mär 23',
    rating: 4.22,
    reviews: 2200
  }, {
    month: 'Apr 23',
    rating: 4.25,
    reviews: 2150
  }, {
    month: 'Mai 23',
    rating: 4.28,
    reviews: 2300
  }, {
    month: 'Jun 23',
    rating: 4.3,
    reviews: 2400
  }, {
    month: 'Jul 23',
    rating: 4.32,
    reviews: 2350
  }, {
    month: 'Aug 23',
    rating: 4.35,
    reviews: 2450
  }, {
    month: 'Sep 23',
    rating: 4.33,
    reviews: 2500
  }, {
    month: 'Okt 23',
    rating: 4.36,
    reviews: 2550
  }, {
    month: 'Nov 23',
    rating: 4.38,
    reviews: 2580
  }, {
    month: 'Dez 23',
    rating: 4.4,
    reviews: 2600
  }, {
    month: 'Jan 24',
    rating: 4.42,
    reviews: 2610
  }]
};
// Simple custom treemap component that doesn't rely on Recharts content prop
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.size, 0);
  // Sort by size descending
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  // Color mapping
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-744">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      const percentage = (item.size / total * 100).toFixed(0);
      // Determine grid span based on size
      let colSpan = 1;
      let rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-745">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-746">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-747">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
export function KonzernStimmungPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedTopic, setSelectedTopic] = useState<(typeof topicClusters)['konzern'][0] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const currentData = regionData[selectedRegion] || regionData.konzern;
  const currentTopics = topicClusters[selectedRegion] || topicClusters.konzern;
  const currentTrend = monthlyTrendData[selectedRegion] || monthlyTrendData.konzern;
  const totalSentiments = currentData.positive + currentData.neutral + currentData.negative;
  const positivePercent = Math.round(currentData.positive / totalSentiments * 100);
  const neutralPercent = Math.round(currentData.neutral / totalSentiments * 100);
  const negativePercent = Math.round(currentData.negative / totalSentiments * 100);
  const handleTopicClick = (topic: any) => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setIsDialogOpen(true);
    }
  };
  const treemapData = currentTopics.map(topic => ({
    name: topic.name,
    size: topic.size,
    sentiment: topic.sentiment
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-748">
      {/* Region Filter */}
      <Card className="!p-4" data-id="element-749">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-750">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-751">
            <Filter size={16} data-id="element-752" />
            <span data-id="element-753">Region:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-754">
            {regions.map(region => {
            const isSelected = selectedRegion === region.id;
            const Icon = region.icon;
            return <button key={region.id} onClick={() => setSelectedRegion(region.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-755">
                  {Icon && <Icon size={14} data-id="element-756" />}
                  {region.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-757">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} change={currentData.trend > 0 ? `+${currentData.trend.toFixed(2)}` : currentData.trend.toFixed(2)} trend={currentData.trend >= 0 ? 'up' : 'down'} icon={<TrendingUp size={18} data-id="element-759" />} subtext="vs. Vormonat" data-id="element-758" />
        <KPICard title="Gesamtbewertungen" value={currentData.total.toLocaleString('de-DE')} change="+8%" trend="up" icon={<MessageSquare size={18} data-id="element-761" />} subtext="diese Periode" data-id="element-760" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} change={`${currentData.positive.toLocaleString('de-DE')}`} trend="up" icon={<ThumbsUp size={18} data-id="element-763" />} subtext="Kommentare" data-id="element-762" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} change={`${currentData.negative.toLocaleString('de-DE')}`} trend="down" icon={<ThumbsDown size={18} data-id="element-765" />} subtext="Kommentare" data-id="element-764" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-766">
        {/* Absolute Resonanz */}
        <Card data-id="element-767">
          <div className="p-6" data-id="element-768">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-769">
              Absolute Resonanz
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-770">
              Verteilung der Sentiment-Analyse
            </p>

            {/* Stacked Bar */}
            <div className="mb-6" data-id="element-771">
              <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-772">
                <div style={{
                width: `${positivePercent}%`,
                backgroundColor: '#639754'
              }} className="flex items-center justify-center" data-id="element-773">
                  {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-774">
                      {positivePercent}%
                    </span>}
                </div>
                <div style={{
                width: `${neutralPercent}%`,
                backgroundColor: '#FFD301'
              }} className="flex items-center justify-center" data-id="element-775">
                  {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-776">
                      {neutralPercent}%
                    </span>}
                </div>
                <div style={{
                width: `${negativePercent}%`,
                backgroundColor: '#E03C32'
              }} className="flex items-center justify-center" data-id="element-777">
                  {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-778">
                      {negativePercent}%
                    </span>}
                </div>
              </div>
            </div>

            {/* Legend with numbers */}
            <div className="space-y-3" data-id="element-779">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-780">
                <div className="flex items-center gap-3" data-id="element-781">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#639754'
                }} data-id="element-782">
                    <ThumbsUp size={18} className="text-white" data-id="element-783" />
                  </div>
                  <div data-id="element-784">
                    <div className="font-semibold text-gray-900" data-id="element-785">Positiv</div>
                    <div className="text-xs text-gray-500" data-id="element-786">
                      Zufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-787">
                  <div className="text-2xl font-bold" style={{
                  color: '#639754'
                }} data-id="element-788">
                    {currentData.positive.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-789">
                    {positivePercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fff9db'
            }} data-id="element-790">
                <div className="flex items-center gap-3" data-id="element-791">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-792">
                    <Minus size={18} className="text-gray-800" data-id="element-793" />
                  </div>
                  <div data-id="element-794">
                    <div className="font-semibold text-gray-900" data-id="element-795">Neutral</div>
                    <div className="text-xs text-gray-500" data-id="element-796">
                      Gemischte Meinungen
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-797">
                  <div className="text-2xl font-bold" style={{
                  color: '#b39500'
                }} data-id="element-798">
                    {currentData.neutral.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-799">
                    {neutralPercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-800">
                <div className="flex items-center gap-3" data-id="element-801">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-802">
                    <ThumbsDown size={18} className="text-white" data-id="element-803" />
                  </div>
                  <div data-id="element-804">
                    <div className="font-semibold text-gray-900" data-id="element-805">Negativ</div>
                    <div className="text-xs text-gray-500" data-id="element-806">
                      Unzufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-807">
                  <div className="text-2xl font-bold" style={{
                  color: '#E03C32'
                }} data-id="element-808">
                    {currentData.negative.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-809">
                    {negativePercent}% aller Bewertungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Themen Cluster Treemap */}
        <Card data-id="element-810">
          <div className="p-6" data-id="element-811">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-812">
              Themen-Cluster
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-813">
              Häufig erwähnte Themen in Bewertungen (klicken für Details)
            </p>

            <div className="h-80" data-id="element-814">
              <SimpleTreemap data={treemapData} onClick={handleTopicClick} data-id="element-815" />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-816">
              <div className="flex items-center gap-2" data-id="element-817">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-818"></div>
                <span className="text-xs text-gray-600" data-id="element-819">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-820">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#FFD301'
              }} data-id="element-821"></div>
                <span className="text-xs text-gray-600" data-id="element-822">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-823">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-824"></div>
                <span className="text-xs text-gray-600" data-id="element-825">Negativ</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 12-Month Trend Chart */}
      <Card data-id="element-826">
        <div className="p-6" data-id="element-827">
          <div className="flex items-center justify-between mb-6" data-id="element-828">
            <div data-id="element-829">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-830">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-831">
                Durchschnittliche Bewertung der letzten 12 Monate
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-832">
              <div className="flex items-center gap-2" data-id="element-833">
                <div className="w-3 h-3 rounded-full bg-edeka-blue" data-id="element-834"></div>
                <span className="text-gray-600" data-id="element-835">Ø Bewertung</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-836">
            <ResponsiveContainer width="100%" height="100%" data-id="element-837">
              <LineChart data={currentTrend} data-id="element-838">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-839" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-840" />
                <YAxis domain={[3.8, 4.6]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => value.toFixed(1)} data-id="element-841" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [value.toFixed(2), name === 'rating' ? 'Ø Bewertung' : 'Bewertungen']} data-id="element-842" />
                <Line type="monotone" dataKey="rating" stroke="#003051" strokeWidth={3} dot={{
                fill: '#003051',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#FFD200',
                stroke: '#003051',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-843" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Trend Summary */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between" data-id="element-844">
            <div className="flex items-center gap-4" data-id="element-845">
              <div className="flex items-center gap-2" data-id="element-846">
                <TrendingUp size={18} style={{
                color: '#639754'
              }} data-id="element-847" />
                <span className="text-sm text-gray-600" data-id="element-848">
                  <span className="font-semibold" style={{
                  color: '#639754'
                }} data-id="element-849">
                    +
                    {((currentTrend[11].rating - currentTrend[0].rating) * 100 / currentTrend[0].rating).toFixed(1)}
                    %
                  </span>{' '}
                  Verbesserung im Jahresvergleich
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-500" data-id="element-850">
              Aktuell:{' '}
              <span className="font-semibold text-gray-900" data-id="element-851">
                {currentTrend[11].rating.toFixed(2)}
              </span>{' '}
              Sterne
            </div>
          </div>
        </div>
      </Card>

      {/* Topic Detail Dialog */}
      <AnimatePresence data-id="element-852">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-853">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-854">
              {/* Dialog Header */}
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-855">
                <div className="flex items-center justify-between" data-id="element-856">
                  <div data-id="element-857">
                    <div className="flex items-center gap-3" data-id="element-858">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-859">
                        {selectedTopic.name}
                      </h3>
                      <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-860">
                        {selectedTopic.sentiment === 'positive' ? 'Positiv' : selectedTopic.sentiment === 'negative' ? 'Negativ' : 'Neutral'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-861">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-862">
                    <X size={20} data-id="element-863" />
                  </button>
                </div>
              </div>

              {/* Dialog Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-864">
                <div className="space-y-4" data-id="element-865">
                  {selectedTopic.comments.map((comment, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-866">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-867">
                        <div className="flex items-center gap-2" data-id="element-868">
                          <MapPin size={14} className="text-edeka-blue" data-id="element-869" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-870">
                            {comment.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-871">
                          <div className="flex" data-id="element-872">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-873">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-874">
                            <Calendar size={12} data-id="element-875" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-876">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-877">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-878">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}